import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import tw from 'twin.macro';
import { CSSProp } from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';

import { SegmentCreatorGarmItem } from './SegmentCreatorGarmItem';
import { SegmentCreatorIabBrandSafetyItem } from './SegmantCreatorIabBrandSafetyItem';
import { SegmentCreatorContextualItem } from './SegmentCreatorContextualItem';
import { SegmentCreatorNamedEntitiesItem } from './SegmentCreatorNamedEntitiesItem';
import { SegmentCreatorAudioCountItem } from './SegmentCreatorAudioCountItem';
import { SegmentCreatorAuthorItem } from './SegmentCreatorAuthorItem';
import { SegmentCreatorShowTitleItem } from './SegmentCreatorShowTitleItem';
import { SegmentCreatorEpisodeTitleItem } from './SegmentCreatorEpisodeTitleItem';
import { SegmentCreatorTranscriptItem } from './SegmentCreatorTranscriptItem';
import { SegmentCreatorRatingScoreItem } from './SegmentCreatorRatingScoreItem';
import { SegmentCreatorReviewCountItem } from './SegmentCreatorReviewCountItem';
import { SegmentCreatorShowTypeItem } from './SegmentCreatorShowTypeItem';
import { SegmentCreatorAppleCategoriesItem } from './SegmentCreatorAppleCategoriesItem';
import { SegmentCreatorPublishedDateItem } from './SegmentCreatorPublishedDateItem';
import { SegmentCreatorLinkedNamedEntitiesItem } from './SegmentCreatorLinkedNamedEntitiesItem';
import { SegmentCreatorDurationItem } from './SegmentCreatorDurationItem';
import { SegmentCreatorEpisodesByShowIdItem } from './SegmentCreatorEpisodesByShowIdItem';
import { SegmentCreatorLanguageItem } from './SegmentCreatorLanguageItem';
import { INTEGRATION_EPISODE_LIMIT } from '@insights_module/constants/integrations';

import {
  ROOT_SEGMENT_ID,
  useSegmentCreatorContext,
} from '@insights_module/context/SegmentCreatorContext';
import { AQLQueryType } from '@insights_module/adc_ql/types';
import { genRandomString } from '@utils/commonUtils';
import { groupQueryDataByGroupId } from '@insights_module/adc_ql/utils';
import { CopyIcon } from '@icons/CopyIcon';
import { TrashCanIcon } from '@icons/TrashCanIcon';
import { PlusIcon } from '@icons/PlusIcon';
import { IDropdownOption, Option } from '@components/Dropdown';
import { CloseIcon } from '@icons/CloseIcon';
import { Info } from '@icons/Info';
import { Loader } from '@components/Loader';
import { useIntegrationsContext } from '@insights_module/context/IntegrationsContext';
import { FEATURE_FLAG, useFeatureFlag } from '@auth_module/hooks/useFeatureFlag';
import { useInsightsContext } from '@insights_module/context/InsightsContext';
import { useFilterTypeOptions } from '@insights_module/hooks/useFilterTypeOptions';
import { AQL_QUERY_TYPE } from '@insights_module/adc_ql/constants';

const SegmentItemWrapper = tw.div`relative flex flex-col gap-1 ml-8 before:(absolute w-7 h-7 left-[-32px] top-0 flex justify-center items-center text-text-and-icon-muted text-[11px]) first:ml-0 first:before:hidden [letter-spacing: -0.22px]`;
const CompositeOperatorWrapper = tw.div`flex gap-1 items-center justify-between`;
const CompositeOperatorButton = tw.div`flex justify-center items-center w-10 border border-gray-1 rounded cursor-pointer text-[11px] font-semibold px-2 py-[3px] text-text-and-icon-muted`;
const SegmentButton = tw.div`flex justify-center items-center cursor-pointer`;
const RootLabelsWrapper = tw.div`text-text-and-icon-muted text-xs font-semibold`;
const TopicsWrapper = tw.div`flex items-center w-full justify-between`;
const TopicsActionWrapper = tw.div`relative flex justify-center items-center`;
const CompositeFooter = tw.div`relative flex gap-2 items-center`;
const Divider = tw.div`h-[20px] w-[1px] border-r border-gray-1`;
const TypePickerWrapper = tw.div`flex flex-col w-[220px] shadow-ui border border-gray-1 rounded-lg bg-neutral-background z-10`;
const TypePickerHeader = tw.div`flex w-full h-[34px] pl-[14px] items-center justify-start text-[11px] font-semibold text-text-and-icon`;
const TypePickerActionWrapper = tw.div`flex flex-col p-1 border-t border-gray-1 text-xs gap-0.5`;
const TypeItemWrapper = tw.div`flex items-center h-[28px] hover:bg-gray-2 rounded cursor-pointer text-text-and-icon`;
const InfoBox = tw.div`flex bg-gray-1 rounded gap-2 items-center py-2.5 pl-4 text-[13px] font-medium my-2 text-text-and-icon-muted`;
const EpisodeCountWrapper = tw.div`flex gap-0.5 items-center text-text-and-icon-muted text-xs font-medium`;
const CountNumber = tw.span`text-[11px] text-text-and-icon-muted mr-[5px]`;

const DEPRECATED_AQL_TYPES: string[] = [
  AQL_QUERY_TYPE.BRAND_SAFETY_IAB,
  AQL_QUERY_TYPE.APPLE_RATING_COUNT,
  AQL_QUERY_TYPE.APPLE_STAR_RATING,
];

type SegmentCreatorItemProps = {
  id: string;
  children?: React.ReactNode;
};

export const negationOptions: IDropdownOption[] = [
  {
    value: 'is',
    label: (
      <Option>
        <Trans i18nKey='insights.query_builder.conditions.is'>Is</Trans>
      </Option>
    ),
  },
  {
    value: 'is_not',
    label: (
      <Option>
        <Trans i18nKey='insights.query_builder.conditions.is_not'>Is not</Trans>
      </Option>
    ),
  },
];

type TypePickerProps = {
  onSelectType: (option: string | null) => void;
  onClickOutside: () => void;
  css?: CSSProp;
};

const TypePicker = ({ onSelectType, onClickOutside, ...rest }: TypePickerProps) => {
  const { t } = useTranslation();
  const { selectedTableType } = useInsightsContext();
  const { type: integrationType } = useIntegrationsContext();
  const ref = useRef<HTMLDivElement>(null);
  const { dropdownOptions } = useFilterTypeOptions();

  const { isEnabled: isNELEnabled } = useFeatureFlag(FEATURE_FLAG.NAMED_ENTITY_LINKING);

  useLayoutEffect(() => {
    const timeout = setTimeout(() => {
      function handleClickOutside(event: MouseEvent) {
        if (ref.current && !ref.current.contains(event.target as Node)) {
          onClickOutside();
        }
      }

      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }, 300);

    return () => clearTimeout(timeout);
  }, []);

  return (
    <TypePickerWrapper ref={ref} {...rest}>
      <TypePickerHeader>{t('insights.query_builder.dimensions')}</TypePickerHeader>
      <TypePickerActionWrapper>
        {dropdownOptions
          .filter(
            ({ meta, value }) =>
              Array.isArray(meta?.for) &&
              meta?.for?.includes(integrationType ? 'episodes' : selectedTableType) &&
              !meta?.read_only &&
              // if the type is linked_named_entities, check if the NEL is enabled before showing it in the dropdown
              (value === 'linked_named_entities' ? isNELEnabled : true)
          )
          .map((option) => (
            <TypeItemWrapper key={option.value} onClick={() => onSelectType(option.value)}>
              {option.label}
            </TypeItemWrapper>
          ))}
      </TypePickerActionWrapper>
    </TypePickerWrapper>
  );
};

export const SegmentCreatorItem = ({ id, children }: SegmentCreatorItemProps) => {
  const { isEnabled: isFlightpathEnabled } = useFeatureFlag(
    FEATURE_FLAG.MEGAPHONE_FLIGHTPATH_INTEGRATION
  );
  const { t } = useTranslation();
  const {
    segmentsData,
    totalEpisodeCount,
    getSegmentData,
    insertSegmentData,
    pushSegmentData,
    getElementIndex,
    removeSegmentData,
    removeGroupSegmentData,
    isPresentational,
    isTotalEpisodesCountLoading,
    queryType,
  } = useSegmentCreatorContext();
  const segmentData = getSegmentData(id);
  const parentSegmentData = getSegmentData(segmentData?.groupId || ROOT_SEGMENT_ID);

  const [showAddFilterGlobally, setShowAddFilterGlobally] = useState(false);
  const [showAddFilterLocally, setShowAddFilterLocally] = useState(false);
  const [filterDropdownPosition, setFilterDropdownPosition] = useState<{ [key: string]: string }>({
    top: '38px',
  });

  const addFilterGlobalyButtonRef = useRef<HTMLDivElement>(null);
  const addFilterLocallyButtonRef = useRef<HTMLDivElement>(null);

  const { type: integrationType } = useIntegrationsContext();
  const { options: FILTERS } = useFilterTypeOptions();

  if (!segmentData) return null;

  function addSegment(groupId: string | null = null, type: AQLQueryType): string | null {
    if (!segmentData) return null;

    if (FILTERS.includes(type)) {
      const newId = genRandomString(16);
      pushSegmentData({ id: newId, groupId, type, negation: false });

      return newId;
    }

    const newGroupId = genRandomString(16);
    const index = getElementIndex(id);
    insertSegmentData(
      [
        { id: newGroupId, groupId, type, negation: false },
        { ...segmentData, id: genRandomString(16), groupId: newGroupId },
      ],
      index
    );
    removeSegmentData(id);

    return newGroupId;
  }

  function handelAddGroup(groupId: string | null = null, type: AQLQueryType) {
    setShowAddFilterLocally(false);
    if (!groupId || !segmentData) return;
    const group = getSegmentData(groupId);
    if (!group) return;

    const newGroupId = addSegment(groupId, group.type === 'all' ? 'any' : 'all');
    if (!newGroupId) return;

    addSegment(newGroupId, type);
  }

  const isComposite: boolean = segmentData.type === 'all' || segmentData.type === 'any';
  const isSingleElementGroup: boolean = useMemo(
    () =>
      groupQueryDataByGroupId(segmentsData).get(segmentData.groupId)?.length === 1 ||
      segmentData.groupId === ROOT_SEGMENT_ID,
    [segmentsData, segmentData.groupId]
  );
  const segmentItemDynamicStyles = useMemo(
    () => [
      isComposite &&
        segmentData.groupId &&
        tw`border border-gray-1 bg-page-background p-1.5 rounded`,
      !segmentData.groupId && tw`border border-gray-1 border-dashed p-4 rounded bg-page-background`,
      parentSegmentData?.type === 'all' && tw`before:content-["and"]`,
      parentSegmentData?.type === 'any' && tw`before:content-["or"]`,
      isSingleElementGroup && tw`before:(top-[6px])`,
    ],
    [isComposite, segmentData.groupId, parentSegmentData?.type, isSingleElementGroup]
  );

  function handleChangeCompositeType(type: 'all' | 'any') {
    if (!segmentData) return;

    pushSegmentData({ ...segmentData, type });
  }

  function handleCopySegment() {
    if (!segmentData) return;

    const newId = genRandomString(16);
    const index = getElementIndex(id);
    insertSegmentData([{ ...segmentData, id: newId }], index + 1);
  }

  function handleDeleteSegment() {
    if (!segmentData) return;
    const groupId = segmentData.groupId;
    if (groupId && groupId !== ROOT_SEGMENT_ID) {
      const groupItems = segmentsData.filter((item) => item.groupId === groupId);
      // if deleting element is the last one in the group, delete the whole group
      if (groupItems.length === 1) {
        removeGroupSegmentData(groupId);
        return;
      }
    }
    removeSegmentData(id);
  }

  function handleSelectedType(type: string | null) {
    setShowAddFilterGlobally(false);
    if (!type || !segmentData?.id) return;
    addSegment(segmentData.id, type as AQLQueryType);
  }

  const setFiltersDropdownStyle = (type: 'globaly' | 'locally') => {
    const buttonRef = type === 'globaly' ? addFilterGlobalyButtonRef : addFilterLocallyButtonRef;
    if (!buttonRef.current) return;

    const buttonPosition = buttonRef.current.getBoundingClientRect();

    if (window.innerHeight - buttonPosition.bottom > 320) {
      setFilterDropdownPosition({ top: '38px' });
    } else {
      setFilterDropdownPosition({ bottom: '38px' });
    }
  };

  const handleAddFilter = (type: 'globaly' | 'locally') => {
    setFiltersDropdownStyle(type);

    type === 'globaly' ? setShowAddFilterGlobally(true) : setShowAddFilterLocally(true);
  };

  return (
    <SegmentItemWrapper css={segmentItemDynamicStyles}>
      {isComposite && (
        <>
          <CompositeOperatorWrapper>
            <div tw='flex gap-1 items-center'>
              {!segmentData.groupId && (
                <RootLabelsWrapper>
                  {t('insights.query_builder.starting_message')}
                </RootLabelsWrapper>
              )}

              <CompositeOperatorButton
                onClick={() => handleChangeCompositeType('all')}
                css={[
                  segmentData.type === 'all' &&
                    tw`border border-accent-muted shadow-dropdown text-text-and-icon`,
                  segmentData.type === 'any' && isPresentational && tw`hidden`,
                  isPresentational && tw`cursor-auto`,
                ]}>
                {t('insights.query_builder.conditions.all')}
              </CompositeOperatorButton>
              <CompositeOperatorButton
                onClick={() => handleChangeCompositeType('any')}
                css={[
                  segmentData.type === 'any' &&
                    tw`border border-accent-muted shadow-dropdown text-text-and-icon`,
                  segmentData.type === 'all' && isPresentational && tw`hidden`,
                  isPresentational && tw`cursor-auto`,
                ]}>
                {t('insights.query_builder.conditions.any')}
              </CompositeOperatorButton>

              {!segmentData.groupId && (
                <RootLabelsWrapper>
                  {t('insights.query_builder.starting_message_suffix')}
                </RootLabelsWrapper>
              )}
            </div>
            {segmentData.groupId && !isPresentational && (
              <SegmentButton tw='pr-[3px]' onClick={() => removeGroupSegmentData(segmentData.id)}>
                <CloseIcon tw='fill-text-and-icon-muted w-2.5 h-2.5' />
              </SegmentButton>
            )}
          </CompositeOperatorWrapper>
          <div tw='[> div]:my-2'>{children}</div>
        </>
      )}

      {segmentsData.length === 1 && !isPresentational && (
        <InfoBox>
          <Info tw='stroke-text-and-icon-muted w-5 h-5' />
          {t('insights.query_builder.empty_query_info')}
        </InfoBox>
      )}

      {FILTERS.includes(segmentData.type) && (
        <TopicsWrapper
          css={[
            isSingleElementGroup &&
              segmentData.groupId === ROOT_SEGMENT_ID &&
              tw`border border-gray-1 bg-neutral-background p-1.5 rounded`,
          ]}>
          {segmentData.type === 'brand_safety_garm' && (
            <SegmentCreatorGarmItem segmentData={segmentData} />
          )}

          {segmentData.type === 'brand_safety_iab' && (
            <SegmentCreatorIabBrandSafetyItem segmentData={segmentData} />
          )}

          {segmentData.type === 'contextual' && (
            <SegmentCreatorContextualItem segmentData={segmentData} />
          )}

          {segmentData.type === 'named_entities' && (
            <SegmentCreatorNamedEntitiesItem segmentData={segmentData} />
          )}

          {segmentData.type === 'linked_named_entities' && (
            <SegmentCreatorLinkedNamedEntitiesItem segmentData={segmentData} />
          )}

          {segmentData.type === 'audio_count' && (
            <SegmentCreatorAudioCountItem segmentData={segmentData} />
          )}

          {segmentData.type === 'author' && <SegmentCreatorAuthorItem segmentData={segmentData} />}

          {segmentData.type === 'name' && <SegmentCreatorShowTitleItem segmentData={segmentData} />}

          {segmentData.type === 'title' && (
            <SegmentCreatorEpisodeTitleItem segmentData={segmentData} />
          )}

          {segmentData.type === 'episodes_by_show_id' && (
            <SegmentCreatorEpisodesByShowIdItem segmentData={segmentData} />
          )}

          {segmentData.type === 'transcript' && (
            <SegmentCreatorTranscriptItem segmentData={segmentData} />
          )}

          {segmentData.type === 'apple_star_rating' && (
            <SegmentCreatorRatingScoreItem segmentData={segmentData} />
          )}

          {segmentData.type === 'apple_rating_count' && (
            <SegmentCreatorReviewCountItem segmentData={segmentData} />
          )}

          {segmentData.type === 'podcast_type' && (
            <SegmentCreatorShowTypeItem segmentData={segmentData} />
          )}

          {segmentData.type === 'apple_categories' && (
            <SegmentCreatorAppleCategoriesItem segmentData={segmentData} />
          )}

          {segmentData.type === 'published_date' && (
            <SegmentCreatorPublishedDateItem segmentData={segmentData} />
          )}

          {segmentData.type === 'duration' && (
            <SegmentCreatorDurationItem segmentData={segmentData} />
          )}

          {segmentData.type === 'language' && (
            <SegmentCreatorLanguageItem segmentData={segmentData} />
          )}

          {!isPresentational && (
            <TopicsActionWrapper>
              {!DEPRECATED_AQL_TYPES.includes(segmentData.type) && (
                <>
                  {isSingleElementGroup && segmentData.groupId === ROOT_SEGMENT_ID && (
                    <SegmentButton
                      ref={addFilterLocallyButtonRef}
                      tw='w-7 h-7 rounded hover:bg-gray-2'
                      css={[showAddFilterLocally && tw`bg-gray-2`]}
                      onClick={() => handleAddFilter('locally')}>
                      <PlusIcon tw='stroke-text-and-icon w-4 h-4' />
                    </SegmentButton>
                  )}
                  {isSingleElementGroup && showAddFilterLocally && (
                    <TypePicker
                      css={[tw`absolute top-[34px] left-0`]}
                      onSelectType={(type) =>
                        handelAddGroup(segmentData.groupId, type as AQLQueryType)
                      }
                      onClickOutside={() => setShowAddFilterLocally(false)}
                    />
                  )}
                  <SegmentButton tw='w-7 h-7 rounded hover:bg-gray-2' onClick={handleCopySegment}>
                    <CopyIcon tw='stroke-text-and-icon' />
                  </SegmentButton>
                </>
              )}

              <SegmentButton tw='w-7 h-7 rounded hover:bg-gray-2' onClick={handleDeleteSegment}>
                <TrashCanIcon tw='stroke-text-and-icon' />
              </SegmentButton>
            </TopicsActionWrapper>
          )}
        </TopicsWrapper>
      )}

      {isComposite && !isPresentational && (
        <CompositeFooter>
          <SegmentButton
            ref={addFilterGlobalyButtonRef}
            tw='w-auto rounded text-accent-1 px-2 py-1.5 text-xs font-semibold gap-1 bg-sec-4 hover:bg-gray-2'
            css={[showAddFilterGlobally && tw`bg-gray-2`, segmentsData.length === 1 && tw`text-sm`]}
            onClick={() => handleAddFilter('globaly')}>
            <PlusIcon tw='stroke-accent-1 w-4 h-4' />
            {t('insights.query_builder.add_filter_button')}
          </SegmentButton>
          {showAddFilterGlobally && (
            <TypePicker
              css={[tw`absolute left-0`, filterDropdownPosition]}
              onSelectType={handleSelectedType}
              onClickOutside={() => setShowAddFilterGlobally(false)}
            />
          )}
          {segmentData.id === ROOT_SEGMENT_ID &&
            !(isFlightpathEnabled && integrationType === 'megaphone') && (
              <>
                <Divider />
                <div tw='text-text-and-icon-muted'>
                  {isTotalEpisodesCountLoading ? (
                    <Loader theme='light' size='sm' />
                  ) : (
                    <>
                      <span tw='text-accent-muted'>
                        {totalEpisodeCount.toLocaleString('en-US')}
                      </span>
                      {queryType === 'audio'
                        ? t('insights.query_builder.footer_audio_message')
                        : t('insights.query_builder.footer_group_message')}
                    </>
                  )}
                </div>
              </>
            )}
        </CompositeFooter>
      )}

      {isPresentational && segmentData.id === ROOT_SEGMENT_ID && integrationType && (
        <EpisodeCountWrapper>
          <CountNumber
            css={[
              Number(totalEpisodeCount) > INTEGRATION_EPISODE_LIMIT[integrationType]
                ? tw`text-error-text-icon`
                : tw`text-success-text-and-icon`,
            ]}>
            {totalEpisodeCount.toLocaleString('en-US')}
          </CountNumber>
          {t('insights.query_builder.tag_view_audio_count_footer_message')}
        </EpisodeCountWrapper>
      )}
    </SegmentItemWrapper>
  );
};
